import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { ApiError, getListPolicyCatalogueQueryKey, getListPolicyScopeRulesQueryKey,
  getGetMyPolicyRequirementsQueryKey, getListMySiteInductionsQueryKey } from '@cdf/api-client-react';
import type { QueryClient } from '@tanstack/react-query';
import { useUser } from '@/components/auth/user-provider';
import { useAuth } from '@/hooks/use-auth';
import { selectedEntity } from '@/features/tenancy/context-navigation';

export function usePolicyAdministrationContext() {
  const user=useUser();
  const {accountKey}=useAuth();
  const {pathname}=useLocation();
  const entity=selectedEntity(user?.tenants??[],pathname);
  const key=[accountKey,user?.appUserId,entity?.id,user?.viewAsRole,[...(user?.capabilities??[])].sort().join(',')].join(':');
  const current=React.useRef(key);
  const mounted=React.useRef(true);
  React.useLayoutEffect(()=>{current.current=key;mounted.current=true;return()=>{mounted.current=false;};},[key]);
  const request={headers:{'X-Tenant-Slug':entity?.baseSlug??''},assertContext:()=>{
    if(!mounted.current || current.current!==key)throw new ApiError('Your access or selected entity changed.',409,'POLICY_CONTEXT_CHANGED');
  }};
  return {key,request,ready:Boolean(user?.appUserId && entity?.id),canAssign:user?.capabilities.includes('inventory:manage')??false};
}
export async function invalidatePolicyAdministration(client:QueryClient) {
  const families:readonly unknown[]=[getListPolicyCatalogueQueryKey()[0],getListPolicyScopeRulesQueryKey()[0],
    getGetMyPolicyRequirementsQueryKey()[0],getListMySiteInductionsQueryKey()[0]];
  await client.invalidateQueries({predicate:q=>families.includes(q.queryKey[0] as string) ||
    (typeof q.queryKey[0]==='string' && /\/(?:policies-and-standards\/catalogue\/[^/]+\/versions|visitor-self\/approvals\/[^/]+\/policies-and-standards)$/.test(q.queryKey[0]))});
}
export function uncertainPolicyWrite(error:unknown) {
  return !(error instanceof ApiError) || error.status===0 || error.status>=500;
}
